// ./src/Pages/Policies/Versions/Form/Components/CreativeFileUpload.jsx
import { useState, useEffect, useRef } from 'react';
import { ProgressBar } from 'Components/common/ProgressBar';
import { ButtonForm } from 'Components/Form/styles';

const formatBytes = bytes => {
  if (!bytes) return '0 B';
  const k = 1024, units = ['B','KB','MB','GB','TB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${units[i]}`;
};

const isAccepted = (file, accept) => {
  if (!accept) return true;
  const rules = accept.split(',').map(s => s.trim().toLowerCase()).filter(Boolean);
  const mime = (file.type || '').toLowerCase();
  const name = (file.name || '').toLowerCase();
  return rules.some(rule => rule.startsWith('.') ? name.endsWith(rule) : mime === rule);
};

function CreativeFileUpload({
  id = 'attachment-upload',
  value,
  onChange,
  accept = '.pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  maxSizeMB = 25,
  helperText = '',
  error = false,
  disabled = false,
}) {
  const [dragOver, setDragOver] = useState(false);
  const [localError, setLocalError] = useState('');
  const [progress, setProgress] = useState(0);
  const inputRef = useRef(null);

  const handlePick = () => inputRef.current?.click();

  const validateAndSet = file => {
    setLocalError('');
    if (!file) return;
    if (!isAccepted(file, accept)) return setLocalError('Tipo de arquivo não suportado.');
    if (file.size > maxSizeMB * 1024 * 1024) return setLocalError(`Arquivo maior que ${maxSizeMB}MB.`);
    setProgress(10);
    const t = setInterval(() => {
      setProgress(p => {
        if (p >= 100) { clearInterval(t); return 100; }
        return p + 15;
      });
    }, 60);
    onChange(file);
  };

  const onInputChange = e => {
    const file = e.target.files?.[0];
    if (file) validateAndSet(file);
    e.target.value = null;
  };

  const onDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) validateAndSet(file);
  };

  const onPaste = e => {
    const item = [...e.clipboardData.items].find(i => i.kind === 'file');
    const file = item?.getAsFile?.();
    if (file) validateAndSet(file);
  };

  useEffect(() => {
    if (!value) setProgress(0);
    else if (progress < 100) setProgress(100);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const removeFile = () => { onChange(null); setProgress(0); setLocalError(''); };
  const openPreview = () => {
    if (!value) return;
    const url = URL.createObjectURL(value);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const fileEmoji = !value ? '📄' : (value.type === 'application/pdf' ? '📄' : '📝');

  return (
    <div style={{ width: '100%' }}>
      <input
        id={id}
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={onInputChange}
        disabled={disabled}
      />

      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onKeyDown={e => !disabled && (e.key === 'Enter' || e.key === ' ') && handlePick()}
        onClick={() => !disabled && handlePick()}
        onDrop={e => !disabled && onDrop(e)}
        onDragOver={e => { if (!disabled) { e.preventDefault(); setDragOver(true); } }}
        onDragLeave={() => !disabled && setDragOver(false)}
        onPaste={e => !disabled && onPaste(e)}
        aria-label="Enviar arquivo. Clique, solte um arquivo aqui, ou cole do clipboard."
        aria-disabled={disabled}
        style={{
          position: 'relative',
          borderRadius: 16,
          padding: 24,
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          opacity: disabled ? 0.6 : 1,
          pointerEvents: disabled ? 'none' : 'auto',
          background: dragOver ? 'color-mix(in srgb, var(--vo-celeste) 6%, transparent)' : 'var(--vo-mist)',
          boxShadow: dragOver ? 'inset 0 0 0 2px color-mix(in srgb, var(--vo-celeste) 35%, transparent)' : 'none',
          transition: 'all .25s ease',
          backgroundImage: dragOver
            ? 'linear-gradient(90deg, color-mix(in srgb, var(--vo-celeste) 12%, transparent), color-mix(in srgb, var(--vo-purple) 12%, transparent), color-mix(in srgb, var(--vo-emerald-600) 12%, transparent))'
            : 'none',
          backgroundSize: dragOver ? '200% 100%' : 'auto',
        }}
        >
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: value ? 8 : 0 }}>
          <div style={{ fontSize: 36, lineHeight: 1 }}>{fileEmoji}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontWeight: 600, fontSize: '1rem' }}>
              {value ? 'Arquivo selecionado' : 'Arraste e solte, clique para escolher, ou cole (Ctrl/Cmd+V)'}
            </div>
            <div style={{ color: 'var(--vo-slate)', fontSize: '0.875rem' }}>
              {value ? `${value.name} · ${formatBytes(value.size)}` : `PDF, DOC ou DOCX · até ${maxSizeMB}MB`}
            </div>
          </div>

            <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
            {!value && ButtonForm && (
                <ButtonForm
                component="span"
                variant="secondary"
                style={{ minWidth: 160 }}
                title="Escolher arquivo"
                >
                  Selecionar arquivo
                </ButtonForm>
            )}

            {value && ButtonForm && (
                <>
                <ButtonForm
                    type="button"
                    variant="secondary"
                    onClick={openPreview}
                    style={{ minWidth: 120 }}
                    title="Abrir pré-visualização"
                >
                  Visualizar
                </ButtonForm>
                <ButtonForm
                    type="button"
                    variant="secondary"
                    onClick={handlePick}
                    style={{ minWidth: 100 }}
                    title="Escolher outro arquivo"
                >
                  Trocar
                </ButtonForm>
                <ButtonForm
                    type="button"
                    variant="secondary"
                    onClick={removeFile}
                    style={{ minWidth: 100 }}
                    title="Remover arquivo"
                >
                  Remover
                </ButtonForm>
                </>
            )}
            </div>
        </div>

        {value && (
          <div style={{ marginTop: 8 }}>
            <ProgressBar value={progress} height={4} />
            <div style={{ color: 'var(--vo-slate)', fontSize: '0.75rem', marginTop: 4 }}>
              {progress < 100 ? 'Validando…' : 'Pronto para enviar'}
            </div>
          </div>
        )}
      </div>

      <div aria-live="polite">
        {(error || localError) ? (
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--vo-danger)', marginTop: 8 }}>
            {localError || helperText}
          </p>
        ) : helperText ? (
          <p style={{ margin: '4px 0 0', fontSize: '12px', color: 'var(--vo-slate)', marginTop: 8 }}>{helperText}</p>
        ) : null}
      </div>
    </div>
  );
}

export default CreativeFileUpload;
